import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AreaChart, Area, Tooltip, ResponsiveContainer, XAxis } from 'recharts';
import { TrendUp } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-zinc-200 shadow-lg px-3 py-2 text-xs rounded-lg" data-testid="revenue-chart-tooltip">
      <p className="font-black text-zinc-900 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-emerald-700">Incassi <strong>€{p.income.toFixed(0)}</strong></p>
      <p className="text-rose-600">Spese <strong>€{p.expenses.toFixed(0)}</strong></p>
      <p className="text-teal-700 border-t border-zinc-100 mt-1 pt-1">Profitto <strong>€{p.profit.toFixed(0)}</strong></p>
    </div>
  );
}

export default function RevenueTrendChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/accounting/revenue-trend?months=6`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setData({ months: [], total_income: 0, total_expenses: 0, total_profit: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 p-5 h-full" data-testid="revenue-chart-loading">
        <div className="animate-pulse space-y-3">
          <div className="h-3 w-1/3 bg-zinc-200 rounded" />
          <div className="h-24 bg-zinc-100 rounded" />
        </div>
      </div>
    );
  }

  const months = data?.months || [];
  const hasData = months.some(m => m.income > 0 || m.expenses > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 }}
      className="bg-white border border-zinc-200 p-5 h-full flex flex-col"
      data-testid="revenue-trend-chart"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
            <TrendUp size={16} weight="bold" className="text-teal-700" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Trend 6 mesi</p>
            <p className="text-sm font-bold text-zinc-900">Andamento incassi</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Totale</p>
          <p className="text-sm font-bold text-teal-700">€{(data?.total_income || 0).toFixed(0)}</p>
        </div>
      </div>

      {hasData ? (
        <div className="flex-1 min-h-[140px]" data-testid="revenue-chart-area">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={months} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="revIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0d9488" stopOpacity={0.55} />
                  <stop offset="95%" stopColor="#0d9488" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#0d9488', strokeOpacity: 0.2, strokeWidth: 30 }} />
              <Area
                type="monotone"
                dataKey="income"
                stroke="#0d9488"
                strokeWidth={2.5}
                fill="url(#revIncome)"
                animationDuration={900}
                animationBegin={200}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-6" data-testid="revenue-chart-empty">
          <TrendUp size={28} className="text-zinc-300 mb-2" />
          <p className="text-xs text-zinc-400 max-w-xs">
            Nessun incasso registrato negli ultimi 6 mesi.<br />
            Il grafico si popolerà quando registrerai i primi bonifici.
          </p>
        </div>
      )}
    </motion.div>
  );
}
